'use client'

import { useEffect, useState } from 'react'
import { Button, Container, Dialog, Image, Navigation, Spacer, Text } from '@vinted/web-ui'
import { X24 } from '@vinted/monochrome-icons'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'

import {
  getPublicPhotos,
  transformPublicPhotosResponse,
} from '@marketplace-web/vas/public-photos-data'
import { navigateToPage } from '@marketplace-web/browser/browser-navigation-util'
import {
  BumpableItemPreCheckoutModel,
  clickBumpMoreItemsEvent,
  viewSoldBumpCelebrationEvent,
} from '@marketplace-web/vas/vas-data'

import { BUMP_MULTIPLE_ITEM_SELECTION_URL } from '../../constants/routes'
import useStartBumpCheckout from '../../hooks/useStartBumpCheckout'

type Props = {
  soldItemId: number
  singleBumpableItemId?: number | null
}

const BumpAgainSuggestionDialog = ({ soldItemId, singleBumpableItemId }: Props) => {
  const { track } = useTracking()
  const translate = useTranslate('bump_items.bump_again_dialog')
  const a11yTranslate = useTranslate('bump_items.a11y')

  const { startCheckout } = useStartBumpCheckout()
  const [isDialogOpen, setIsDialogOpen] = useState(true)

  const {
    fetch: fetchSoldItemPhoto,
    transformedData: itemPhotoData,
    error,
  } = useFetch(getPublicPhotos, transformPublicPhotosResponse)

  const isDialogReadyToBeShown = !!itemPhotoData || !!error

  useEffect(() => {
    fetchSoldItemPhoto({
      itemIds: [soldItemId],
    })

    track(viewSoldBumpCelebrationEvent({ screen: 'current_user_profile' }))
  }, [soldItemId, fetchSoldItemPhoto, track])

  const handleDialogClose = () => {
    setIsDialogOpen(false)
  }

  const handleActionClick = () => {
    track(clickBumpMoreItemsEvent({ screen: 'current_user_profile' }))

    if (!singleBumpableItemId) {
      navigateToPage(BUMP_MULTIPLE_ITEM_SELECTION_URL)

      return
    }

    const selectedItem: BumpableItemPreCheckoutModel = {
      id: singleBumpableItemId,
      title: null,
      thumbnail: null,
    }

    setIsDialogOpen(false)
    startCheckout(selectedItem)
  }

  if (!isDialogReadyToBeShown) return null

  return (
    <Dialog
      show={isDialogOpen}
      closeOnOverlay
      defaultCallback={handleDialogClose}
      testId="bump-again-suggestion-dialog"
    >
      <Navigation
        theme="transparent"
        right={
          <Button
            styling={Button.Styling.Flat}
            onClick={handleDialogClose}
            iconName={X24}
            theme="amplified"
            inline
            testId="bump-again-suggestion-dialog--close-button"
            aria={{ 'aria-label': a11yTranslate('actions.close') }}
          />
        }
      />
      <div className="u-flexbox u-justify-content-center">
        {!!itemPhotoData?.photos.length && (
          <Image
            src={itemPhotoData?.photos[0]?.url}
            size={Image.Size.X2Large}
            styling={Image.Styling.Rounded}
            ratio={Image.Ratio.SmallPortrait}
            testId="bump-again-suggestion-dialog--item-photo"
          />
        )}
      </div>
      <Spacer size={Spacer.Size.Regular} />
      <Container>
        <Text
          as="h2"
          type={Text.Type.Heading}
          text={translate('title')}
          alignment={Text.Alignment.Center}
          width={Text.Width.Parent}
        />
        <Spacer size={Spacer.Size.Small} />
        <Text
          as="p"
          type={Text.Type.Body}
          text={translate('body')}
          alignment={Text.Alignment.Center}
          width={Text.Width.Parent}
        />
      </Container>
      <Container>
        <Button
          text={translate('action')}
          styling={Button.Styling.Filled}
          onClick={handleActionClick}
          testId="bump-again-suggestion-dialog--action-button"
        />
      </Container>
    </Dialog>
  )
}

export default BumpAgainSuggestionDialog
